import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { BLOG_STALE_TIME, blogApi, type BlogLang } from '../../api/blog';
import { useLandingPrefix } from './LandingShell';
import { PostCard } from './PostCard';

/** Three, because that is one row of the grid; a fourth would sit alone on a line of its own. */
const SHOWN = 3;

/**
 * The newest articles, on the homepage.
 *
 * The homepage is the page other sites link to, so a link from it is the strongest one the site
 * can give its own articles — and the difference between a new article being found the day it is
 * published and whenever a crawler next reads the sitemap.
 *
 * Renders nothing at all until there is something to show: before the first article exists, and
 * whenever the request fails, the homepage is simply the homepage it was.
 */
export function LatestPosts() {
  const { t } = useTranslation();
  const prefix = useLandingPrefix();

  const lang: BlogLang = prefix === '/en' ? 'en' : 'bg';
  const locale = lang === 'en' ? 'en-GB' : 'bg-BG';

  const { data } = useQuery({
    // The same key as the first page of the index, so opening the blog from here costs no request.
    queryKey: ['blog-index', lang, null, 1],
    queryFn: () => blogApi.posts(lang, 1),
    staleTime: BLOG_STALE_TIME,
    retry: false,
  });

  const posts = data?.items.slice(0, SHOWN) ?? [];

  if (posts.length === 0) {
    return null;
  }

  return (
    <section className="l-section">
      <div className="l-latest-head">
        <div>
          <p className="l-eyebrow">{t('blog.eyebrow')}</p>
          <h2>{t('blog.latestTitle')}</h2>
        </div>
        <Link to={`${prefix}/blog`}>{t('blog.allPosts')} →</Link>
      </div>

      <div className="l-post-grid">
        {posts.map((post) => (
          <PostCard key={post.slug} post={post} prefix={prefix} locale={locale} />
        ))}
      </div>
    </section>
  );
}
